import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { api } from '@/api';
import { ALL_OPTION_KEY, RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';

import { useDownloadCSV } from '../../hooks';
import { PlanOmnibusBalanceReportTableParams } from '../PlanOmnibusBalanceReport.types';

export const QUERY_KEY = 'EXPORT_PLAN_OMNIBUS_BALANCE_REPORT_CSV';
const usePlanOmnibusBalanceReportExportCSVQuery = (params: PlanOmnibusBalanceReportTableParams) => {
  const handleSaveFile = useDownloadCSV();
  const [isLoading, setIsLoading] = useState(false);
  const formattedStartDate = useMemo(() => {
    if (!params.startDate) {
      return null;
    }
    const parsedDate = dayjs(params.startDate);
    if (!parsedDate.isValid()) {
      return null;
    }
    return parsedDate.format(RAW_DATE_FORMAT);
  }, [params.startDate]);

  const formattedEndDate = useMemo(() => {
    if (!params.endDate) {
      return null;
    }
    const parsedDate = dayjs(params.endDate);
    if (!parsedDate.isValid()) {
      return null;
    }
    return parsedDate.format(RAW_DATE_FORMAT);
  }, [params.endDate]);

  const contributionStartDate = useMemo(() => {
    return params.isContributionMode ? formattedStartDate : null;
  }, [formattedStartDate, params.isContributionMode]);

  const contributionEndDate = useMemo(() => {
    return params.isContributionMode ? formattedEndDate : null;
  }, [formattedEndDate, params.isContributionMode]);

  const transactionStartDate = useMemo(() => {
    return params.isContributionMode ? null : formattedStartDate;
  }, [formattedStartDate, params.isContributionMode]);
  const transactionEndDate = useMemo(() => {
    return params.isContributionMode ? null : formattedEndDate;
  }, [formattedEndDate, params.isContributionMode]);

  const { refetch } = useQuery(
    [QUERY_KEY],
    () =>
      api.get(API_URL.REPORTS_PLAN_OMNIBUS_BALANCE_REPORT_BREAKDOWN_EXPORT_CSV, {
        params: _.pickBy({
          employer_id: params.employerId,
          show_ssn: params.isSSNShowed,
          contribution_start_date: contributionStartDate,
          contribution_end_date: contributionEndDate,
          transaction_start_date: transactionStartDate,
          transaction_end_date: transactionEndDate,
          search_value: params.searchString,
          plan_ids:
            params?.reportPlanIds && !params?.reportPlanIds.includes(ALL_OPTION_KEY)
              ? params?.reportPlanIds.join(',')
              : undefined,
        }),
      }),
    {
      retry: 0,
      enabled: false,
    }
  );

  const handleExport = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await refetch();
      handleSaveFile(response.data?.data, 'csv', 'plan_omnibus_balance');
    } finally {
      setIsLoading(false);
    }
  }, [handleSaveFile, refetch]);
  return {
    isLoading,
    handleExport,
  };
};

export default usePlanOmnibusBalanceReportExportCSVQuery;
